import { MoveRight, TrendingDown, TrendingUp, MapPin, Calendar, Store, ChevronRight, ArrowRight } from 'lucide-react';
import React, { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

interface PriceMaketProductProps {
  product_name: string;
  average_price: string;
  collect_type: string;
  market_name: string;
  arrondissement: string;
  latest_price: string;
  previous_price: string;
  showMarketName: boolean;
  showRegion?: boolean;
  region?: string;
  department?: string;
  showDepartement?: boolean;
  image_url?: string;
  product_category?: string;
  lastUpdate?: string;
  product?:any
  onclick: MouseEventHandler;
}

function PriceMaketProduct({
  product_name,
  average_price,
  collect_type,
  market_name,
  arrondissement,
  latest_price,
  previous_price,
  showMarketName,
  showRegion,
  region,
  department,
  showDepartement,
  image_url,
  product_category,
  lastUpdate,
  product,
  onclick,
}: PriceMaketProductProps) {
  const { t } = useTranslation();

  function calcPriceDiff() {
    if (previous_price === null || previous_price === undefined || previous_price === '') {
      return 0;
    }
    const last = Number(latest_price.replace(/\s/g, ""));
    const prev = Number(previous_price.replace(/\s/g, ""));
    if (isNaN(last) || isNaN(prev)) {
      return 0;
    }
    return last - prev;
  }

  console.log(product)

  const diff = calcPriceDiff();
  const isIncrease = diff > 0;
  const isDecrease = diff < 0;
  const isStable = diff === 0;

  const getVariationIcon = () => {
    if (isDecrease) return <TrendingDown className="w-4 h-4 text-green-600" />;
    if (isIncrease) return <TrendingUp className="w-4 h-4 text-red-600" />;
    if(isStable) return <MoveRight  className="w-4 h-4 text-gray-600"/>
    return null;
  };

  const getVariationColor = () => {
    if (isDecrease) return 'text-green-600';
    if (isIncrease) return 'text-red-600';
    return 'text-gray-500';
  };
    const getVariationBg = () => {
    if (isDecrease) return 'bg-green-100';
    if (isIncrease) return 'bg-red-100';
    return 'bg-gray-100';
  };

 const getVariationBorder = () => {
  if (isDecrease) return 'hover:border-t-primary';
  if (isIncrease) return 'hover:border-t-secondary';

  return 'hover:border-t-gray-500';
};

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`group bg-white rounded-xl border border-gray-200 ${getVariationBorder()} hover:border-t-4 hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden h-full flex flex-col`}
      onClick={onclick}
    >
      {/* En-tête: Date + variation */}
      <div className="flex items-center justify-between px-4 pt-4 min-h-[32px]">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Calendar className="w-3.5 h-3.5" />
          <span className="whitespace-nowrap">{lastUpdate}</span>
        </div>
        <div className={`flex items-center ${getVariationBg()} gap-1.5 flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium`}>
          {getVariationIcon()}
          <span className={getVariationColor()}>
            {isStable ? t('not_change') : (isDecrease ? t('baisse') || 'Baisse' : t('hausse') || 'Hausse')}
          </span>
        </div>
      </div>

      <div className="p-4 pt-2 h-full flex flex-col">
        {/* Image + infos produit */}
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 md:w-16 md:h-16 flex-shrink-0 border border-[#dfe1df] rounded-xl overflow-hidden bg-gray-100 ring-1 ring-gray-100">
            <img
              src={image_url || '/assets/logo.png'}
              alt={product_name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/assets/product-placeholder.jpg';
              }}
            />
          </div>

          <div className="flex-1 min-w-0 flex flex-col gap-1">
            <h3 className="text-sm md:text-base font-bold text-gray-900 truncate group-hover:text-primary transition-colors">
              {product_name}
            </h3>
            <p className="text-xs text-gray-500 truncate">
               {t('collect_type')}: {collect_type}
            </p>
            <div className="flex items-center gap-1 text-xs text-gray-400 truncate mt-auto">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-secondary" />
              <span className="truncate">{arrondissement}</span>
              {showMarketName && market_name && (
                <>
                  <span className="text-gray-300 flex-shrink-0">•</span>
                  <span className="truncate">{market_name}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Région / département */}
        {(showRegion || showDepartement) && (
          <div className="mt-1 ml-16 flex items-center gap-3 text-xs text-gray-400 truncate">
            {showRegion && region && <span className="truncate">{region}</span>}
            {showDepartement && department && <span className="truncate">{department}</span>}
          </div>
        )}

        {/* Prix */}
        <div className="mt-auto pt-4">
          <div className="flex items-center justify-between bg-gradient-to-b from-[#fafbfa] to-[#f4f5f4] rounded-xl p-3">
            {latest_price && (
              <div className="flex flex-col flex-1">
                <p className="text-[10px] text-[#757a75] font-medium uppercase tracking-wider">
                  {t('last_price')}
                </p>
                <span className="text-sm text-gray-400 line-through">
                  {previous_price} <span className="text-[10px] line-through font-bold text-[#888] whitespace-nowrap">FCFA</span>
                </span>
              </div>
            )}
            {latest_price && <div className="border-l border-dashed border-x-lime-600 self-stretch" />}
            <div className="flex flex-col items-end flex-1">
              <p className="text-[10px] text-[#757a75] font-medium uppercase tracking-wider">
                {t('new_price')}
              </p>
              <span className="text-xl tracking-tighter font-bold text-primary whitespace-nowrap">
                {latest_price}
                <span className="text-[11px] font-bold text-[#333] whitespace-nowrap"> FCFA</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default PriceMaketProduct;